import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ReactFlowProvider, useReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

// Workflow components
import WorkFlow from "../components/WorkFlow/index.js";
import NodesPanel from "../views/details/components/NodesPanel.js";
import Setting from "../views/details/components/Setting.js";

// Store and utils
import useStore from "../components/WorkFlow/store/index.js";
import { ensureSingleStartNode } from "../components/WorkFlow/utils/index.js";
import { extractGreetingTemplateFromWorkflow } from "../lib/workflowGreeting.js";
import { debounce } from "../lib/debounce.js";

function WorkflowEditInner({
  initialData = { nodes: [], edges: [], agent: {} },
  /** Changes only when script is loaded/saved — not on every parent re-render */
  workflowLoadKey = "",
  scriptId,
  hideAgentSettings = false,
  hideSaveButton = false,
  externalAgent = null,
  onSaveDraft = null,
  onWorkflowChange = null,
  onRegisterFlush = null,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** Feeds WorkFlow layout only on load/save — not on every parent sync (avoids reset loops) */
  const [canvasSeed, setCanvasSeed] = useState({ nodes: [], edges: [] });
  const skipNextSyncRef = useRef(false);

  const setAgent = useStore((state) => state.setAgent);
  const setNodes = useStore((state) => state.setNodes);
  const setEdges = useStore((state) => state.setEdges);
  const { getNodes, getEdges } = useReactFlow();

  const buildWorkflowJson = useCallback(() => {
    const agent = hideAgentSettings && externalAgent != null
      ? { ...useStore.getState().agentSetting, ...externalAgent }
      : useStore.getState().agentSetting;
    return { nodes: getNodes(), edges: getEdges(), agent };
  }, [hideAgentSettings, externalAgent, getNodes, getEdges]);

  const flushWorkflowToParent = useCallback(() => {
    if (!onWorkflowChange) return;
    skipNextSyncRef.current = true;
    onWorkflowChange(JSON.stringify(buildWorkflowJson(), null, 2));
  }, [onWorkflowChange, buildWorkflowJson]);

  const debouncedWorkflowChange = useMemo(
    () => debounce(() => flushWorkflowToParent(), 400),
    [flushWorkflowToParent]
  );

  useEffect(() => {
    if (typeof onRegisterFlush === "function") {
      onRegisterFlush(flushWorkflowToParent);
      return () => onRegisterFlush(null);
    }
    return undefined;
  }, [onRegisterFlush, flushWorkflowToParent]);

  // Reload canvas only when script is opened or saved — not when Agent settings tab opens
  useEffect(() => {
    setIsLoading(true);
    try {
      const data = initialData || {};
      const nodes = ensureSingleStartNode(data.nodes || []);
      const edges = data.edges || [];
      setCanvasSeed({ nodes, edges });
      setNodes(nodes);
      setEdges(edges);
      const agent = hideAgentSettings && externalAgent
        ? { ...(data.agent || {}), ...externalAgent }
        : (data.agent || {});
      setAgent(agent);
      skipNextSyncRef.current = true;
    } catch (err) {
      console.error("Failed to load workflow data:", err);
      setCanvasSeed({ nodes: [], edges: [] });
      setNodes([]);
      setEdges([]);
      setAgent({});
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialData tied to workflowLoadKey
  }, [workflowLoadKey]);

  // Merge parent agent settings without resetting nodes/edges
  useEffect(() => {
    if (!hideAgentSettings || !externalAgent) return;
    const current = useStore.getState().agentSetting;
    const changed = Object.entries(externalAgent).some(
      ([key, val]) => current[key] !== val
    );
    if (!changed) return;
    setAgent({ ...current, ...externalAgent });
  }, [hideAgentSettings, externalAgent, setAgent]);

  // Keep parent editedScript in sync (does not feed back into WorkFlow — canvasSeed is stable)
  useEffect(() => {
    if (!onWorkflowChange || isLoading) return;

    let prevNodes = useStore.getState().nodes;
    let prevEdges = useStore.getState().edges;

    const unsubscribe = useStore.subscribe((state) => {
      if (skipNextSyncRef.current) {
        skipNextSyncRef.current = false;
        return;
      }
      if (state.nodes === prevNodes && state.edges === prevEdges) return;
      prevNodes = state.nodes;
      prevEdges = state.edges;
      debouncedWorkflowChange();
    });

    return unsubscribe;
  }, [isLoading, onWorkflowChange, debouncedWorkflowChange]);

  const handleUpdateScript = async () => {
    const workflowJson = buildWorkflowJson();
    if (!scriptId && typeof onSaveDraft === 'function') {
      setSaving(true);
      try {
        await onSaveDraft(workflowJson);
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!scriptId) return alert("⚠️ No script selected to update.");

    console.log("📥 Saving workflow JSON:", workflowJson);

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const res = await fetch(`${apiBase}/api/update-script`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          _id: scriptId,
          script: JSON.stringify(workflowJson, null, 2),
          greetingTemplate: extractGreetingTemplateFromWorkflow(workflowJson) || undefined,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        alert("✅ Script updated successfully!");
        // ✅ update local state immediately
        setAgent(workflowJson.agent || {});
        setCanvasSeed({
          nodes: ensureSingleStartNode(workflowJson.nodes || []),
          edges: workflowJson.edges || [],
        });
      } else {
        alert(`❌ Error updating script: ${data.message || "Unknown error."}`);
      }
    } catch (err) {
      console.error("❌ Update failed:", err);
      alert("❌ Failed to update script.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      <main className={`flex-1 flex overflow-hidden min-h-0 ${hideAgentSettings ? "bg-[#F9F9F9]" : "bg-[#F5F7FA]"}`}>
        <div className="flex-1 relative min-w-0 min-h-0">
          <div className="absolute left-3 top-3 z-10">
            <NodesPanel variant="dropdown" />
          </div>
          {hideAgentSettings && !hideSaveButton && (
            <div className="absolute top-2 right-2 z-10">
              <button
                type="button"
                onClick={handleUpdateScript}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg bg-[#459EA3] hover:bg-[#3d8a8e] text-white text-sm font-medium shadow disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save workflow"}
              </button>
            </div>
          )}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
              <div className="text-lg font-semibold text-gray-700">
                Loading...
              </div>
            </div>
          )}
          <WorkFlow
            isLoading={isLoading}
            nodesData={canvasSeed.nodes}
            edgesData={canvasSeed.edges}
          />
        </div>

        {!hideAgentSettings && (
          <Setting onUpdateScript={handleUpdateScript} saving={saving} />
        )}
      </main>
    </div>
  );
}

function WorkflowEdit(props) {
  return (
    <ReactFlowProvider>
      <WorkflowEditInner {...props} />
    </ReactFlowProvider>
  );
}

export default WorkflowEdit;
